import React, { forwardRef } from 'react'

import {
  ComboBox,
  ComboBoxInputGroup,
  ComboBoxPopover,
  Input, Label,
  ListBox,
  ListBoxItem,
} from '@heroui/react'
import { useTranslation } from 'react-i18next'

interface AutocompleteProps {
  label?: string;
  placeholder?: string;
  value: string;
  onChange: (val: string) => void;
  suggestions?: string[];
  required?: boolean;
  className?: string;
  variant?: any;
  size?: any;
  isInvalid?: boolean;
  errorMessage?: string;
  onBlur?: React.FocusEventHandler;
  onFocus?: React.FocusEventHandler;
  name?: string;
  onOpenChange?: (isOpen: boolean) => void;
  isOpen?: boolean;
  menuTrigger?: 'focus' | 'input' | 'manual';
  isLoading?: boolean;
}

export const Autocomplete = forwardRef<HTMLInputElement, AutocompleteProps>(({
  label,
  placeholder,
  value,
  onChange,
  suggestions = [],
  required,
  className = '',
  variant = 'secondary',
  size = 'md',
  isInvalid,
  onBlur,
  onFocus,
  name,
  onOpenChange,
  isOpen,
  menuTrigger,
  isLoading,
}, ref) => {
  const { t } = useTranslation()

  const loadingText = t('common.loading', 'Загрузка...')
  const emptyText = t('common.no_results', 'Результатов не найдено')
  const initialText = t('common.start_typing', 'Начните вводить...')

  const items = isLoading && suggestions.length === 0
    ? [{ id: '__loading__', textValue: loadingText }]
    : suggestions.length === 0 && value.length > 0
      ? [{ id: '__empty__', textValue: emptyText }]
      : suggestions.length === 0
        ? [{ id: '__empty_initial__', textValue: initialText }]
        : suggestions.map(s => ({ id: s, textValue: s }))

  return (
    <ComboBox
      className={`w-full ${className}`}
      isRequired={required}
      inputValue={value}
      onInputChange={onChange}
      onSelectionChange={key => key && onChange(String(key))}
      allowsCustomValue={true}
      isInvalid={isInvalid}
      name={name}
      isOpen={isOpen !== undefined ? isOpen : undefined}
      onOpenChange={onOpenChange}
      menuTrigger={menuTrigger}
      {...(isLoading !== undefined ? { isLoading } as any : {})}
      items={items}
    >
      {label && <Label>{label}</Label> }
      <ComboBoxInputGroup className="relative flex items-center">
        <Input
          ref={ref}
          value={value}
          onChange={e => onChange(e.target.value)}
          onBlur={onBlur}
          onFocus={onFocus}
          onKeyDown={e => {
            if (e.key === ' ') {
              e.stopPropagation()
            }
          }}
          placeholder={placeholder}
          variant={variant}
          size={size}
          className="w-full"
        />
      </ComboBoxInputGroup>
      <ComboBoxPopover>
        <ListBox items={items}>
          {(item: any) => (
            <ListBoxItem 
              id={item.id} 
              textValue={item.textValue} 
              // @ts-ignore
              isDisabled={item.id === '__loading__' || item.id === '__empty__' || item.id === '__empty_initial__'}
            >
              {item.textValue}
            </ListBoxItem>
          )}
        </ListBox>
      </ComboBoxPopover>
    </ComboBox>
  )
})

Autocomplete.displayName = 'Autocomplete'
